import React from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button';
import LoadingButton from '@atlaskit/button/loading-button';
import DownloadIcon from '@atlaskit/icon/glyph/download';
import { N800 } from '@atlaskit/theme/colors';
import FileIcon from '@atlaskit/icon/glyph/file';
import ToolTip from '@atlaskit/tooltip';
import { REFUND_PATH_2, NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { isInvoiceRefunded } from '@atlassian/cssxp-utils-invoice';
import { Refund } from '@atlassian/cssxp-types-billing';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
import { FormattedDate, HeadCell, SearchResultColumn, OpenSidebarRow } from '@css-xp/base';
import { Refunds } from '@css-xp/graphtypes/src/generated-types-new';
import { useCreditNotePoller } from '@css-xp/ppc-refund-table';
import { InvoiceRefundStatusLozenge } from '@css-xp/billing-v2/invoices/src/components/invoice-refund-status-lozenge';
import { InvoiceRefundCreditNote } from '@css-xp/billing-v2/invoices/src/components/invoice-refund-creditnote-lozenge';
import { FormatCurrency, NoData } from '@css-xp/ppc-common';
import { InvoiceTableColumns } from './invoice-table';

type ColumnList = HeadCell & SearchResultColumn;

interface RefundChildRowProps {
  key: string;
  refund: Refunds;
  isSelected: boolean;
  index?: number;
  refundDetailsLink?: string | undefined;
  shouldDisableSidebar?: boolean;
  visibleColumns: ColumnList[];
  transactionAccountId?: string;
  isCreditNoteIconFunctional?: boolean;
  isDesignIteration1Enabled?: boolean;
}

export const RefundChildRow = (refundChildRowProps: RefundChildRowProps) => {
  const {
    refund,
    isSelected,
    refundDetailsLink,
    shouldDisableSidebar = false,
    visibleColumns,
    transactionAccountId,
    isCreditNoteIconFunctional = false,
    isDesignIteration1Enabled,
  } = refundChildRowProps;
  const { handleCreditNoteDownload, isDownloadingPdf } = useCreditNotePoller(
    refund?.creditNoteId as string,
    transactionAccountId as string
  );

  const getCreditNoteCell = () => {
    if (isCreditNoteIconFunctional) {
      return (
        <div className="credit-note-icon-with-number-v2">
          <LoadingButton
            isLoading={isDownloadingPdf}
            onClick={() => {
              handleCreditNoteDownload(refund?.id);
            }}
            iconBefore={
              <ToolTip content={<>Download credit note</>} position="bottom-start" tag="span">
                <DownloadIcon
                  aria-label="Download credit note PDF"
                  label="Download credit note PDF"
                  size="medium"
                  testId="download-credit-note-cloud-icon"
                  primaryColor={N800}
                />
              </ToolTip>
            }
          />
          <Link
            to={refundDetailsLink ?? `${REFUND_PATH_2}/${encodeURIComponent(refund.creditNoteNumber || refund.id)}`}
            onClick={(e) => e.stopPropagation()}
            className="avatar-container"
          >
            <span className="invoice-refund-slug-cell">{refund.creditNoteNumber || refund.id}</span>
          </Link>
        </div>
      );
    }
    return (
      <Link
        to={refundDetailsLink ?? `${REFUND_PATH_2}/${encodeURIComponent(refund.creditNoteNumber || refund.id)}`}
        onClick={(e) => e.stopPropagation()}
        className="avatar-container"
      >
        <span className="invoice-refund-slug-cell">
          <FileIcon label="file icon" primaryColor={token('color.icon')} /> {refund.creditNoteNumber || refund.id}
        </span>
      </Link>
    );
  };

  const getRefundData = () => {
    return visibleColumns.map((column) => {
      switch (column.name) {
        case InvoiceTableColumns.INVOICE_NUMBER:
          return (
            <td className="id col-auto" data-testid="refund-id">
              {getCreditNoteCell()}
              <div className="invoice-refund-status">
                {!isInvoiceRefunded(refund as Refund) && (
                  <span className="pl-8">{<InvoiceRefundStatusLozenge status={'PENDING'} />}</span>
                )}
                {refund?.type !== 'full-item-refund' && (
                  <>
                    {refund?.type && (
                      <span className="pl-8">{<InvoiceRefundCreditNote credittype={refund.type} />}</span>
                    )}
                  </>
                )}
              </div>
            </td>
          );
        case InvoiceTableColumns.STATUS:
        case InvoiceTableColumns.COMPANY:
        case InvoiceTableColumns.CUSTOMER:
        case InvoiceTableColumns.SOLD_TO_ADDRESS:
        case InvoiceTableColumns.PAYMENT_METHOD:
        case InvoiceTableColumns.PO_NUMBER:
        case InvoiceTableColumns.DUE:
        case InvoiceTableColumns.OUTSTANDING_BALANCE:
        case InvoiceTableColumns.DUNNING_DATE:
        case InvoiceTableColumns.BILLING_PROFILE:
          return <td></td>;
        case InvoiceTableColumns.ISSUED:
          return (
            <td>
              {refund.createdDate ? (
                <FormattedDate date={refund.createdDate} showTime={true} format={NEW_DATE_FORMAT} />
              ) : (
                <NoData />
              )}
            </td>
          );
        case InvoiceTableColumns.AMOUNT:
          return (
            <td
              className={classNames('invoice-amount-cell', { 'text-align-right': isDesignIteration1Enabled })}
              data-testid="refund-amount"
            >
              {refund.total ? (
                <>
                  - <FormatCurrency value={refund.total} currency={refund.currency} showCurrencyCode showCommas />
                </>
              ) : null}
            </td>
          );
      }
    });
  };

  return (
    <>
      {isSelected || shouldDisableSidebar ? (
        <tr key={refund.id} className="border-top refund-row" data-testid="refund-normal-row">
          {getRefundData()}
        </tr>
      ) : (
        <OpenSidebarRow
          key={refund.id}
          className="border-top"
          sidebar={GloballyAllowedSidebar.REFUND_PREVIEW}
          payload={{
            refundId: refund.creditNoteNumber || refund.id,
          }}
          sidebarOptions={{
            isOverlay: true,
            sidebarWidth: '600px',
          }}
          isSelected={isSelected}
          hasDefaultClassName={false}
        >
          {getRefundData()}
        </OpenSidebarRow>
      )}
    </>
  );
};

export default RefundChildRow;
